import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { MONTHS } from "@/lib/helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { History, Undo2 } from "lucide-react";

const statusColor = {
  berhasil: "bg-emerald-100 text-emerald-800",
  "berhasil sebagian": "bg-amber-100 text-amber-800",
  gagal: "bg-rose-100 text-rose-800",
};

export default function RiwayatUpload() {
  const { isSuperAdmin } = useAuth();
  const [logs, setLogs] = useState([]);

  const load = () => api.get("/upload-log").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);

  const batalkan = async (id) => {
    try {
      const res = await api.delete(`/upload-log/${id}`);
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="riwayat-page">
      <PageHeader icon={History} title="Riwayat Upload" subtitle="Audit trail semua upload: siapa, kapan, indikator, bulan, dan status." />

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <EmptyState icon={History} title="Belum ada riwayat" desc="Riwayat upload akan tampil di sini." />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Tanggal Upload</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Indikator</TableHead>
                    <TableHead>Periode</TableHead>
                    <TableHead>Oleh</TableHead>
                    <TableHead className="text-right">Baris</TableHead>
                    <TableHead>Status</TableHead>
                    {isSuperAdmin && <TableHead className="text-right">Aksi</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id} data-testid={`log-row-${l.id}`}>
                      <TableCell className="text-slate-600 text-sm">{new Date(l.tanggal_upload).toLocaleString("id-ID")}</TableCell>
                      <TableCell className="font-medium">{l.nama_file}</TableCell>
                      <TableCell><Badge variant="secondary">{l.indikator}</Badge></TableCell>
                      <TableCell>{MONTHS[l.bulan - 1]?.nama} {l.tahun}</TableCell>
                      <TableCell className="text-slate-600">{l.diupload_oleh}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.jumlah_baris_masuk}
                        {l.jumlah_baris_gagal > 0 && <span className="text-rose-500"> / {l.jumlah_baris_gagal} gagal</span>}
                      </TableCell>
                      <TableCell><span className={`px-2 py-1 rounded-md text-xs font-semibold ${statusColor[l.status] || "bg-slate-100"}`}>{l.status}</span></TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`batalkan-upload-${l.id}`}>
                                <Undo2 className="w-4 h-4 mr-1.5" /> Batalkan
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Batalkan upload ini?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Semua record capaian dari upload <b>{l.nama_file}</b> ({l.indikator} — {MONTHS[l.bulan - 1]?.nama} {l.tahun}) akan
                                  dihapus permanen, dan baris ini hilang dari riwayat. Jika upload ini pernah menimpa data upload sebelumnya,
                                  data lama tidak kembali otomatis. Tindakan ini tidak bisa dibatalkan.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => batalkan(l.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-batalkan-upload-${l.id}`}>
                                  Ya, batalkan &amp; hapus
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
